import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Divider,
  Container,
  Paper,
  useTheme,
  alpha,
  LinearProgress,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Switch,
} from '@mui/material';
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  Visibility,
  VisibilityOff,
  Business as BusinessIcon,
  CheckCircle,
  Cancel,
  ArrowBack as ArrowBackIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
} from '@mui/icons-material';
import api from '../services/api';
import { Helmet } from 'react-helmet-async';
import LegalBusinessDetails from './LegalBusinessDetails';

const Register = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    companyName: '',
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [verificationState, setVerificationState] = useState(null);
  const [infoMessage, setInfoMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });
  const navigate = useNavigate();
  const theme = useTheme();

  const toggleDarkMode = () => {
    setDarkMode((prev) => {
      const newMode = !prev;
      localStorage.setItem('darkMode', JSON.stringify(newMode));
      window.location.reload();
      return newMode;
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const validateForm = () => {
    if (!formData.companyName.trim()) {
      setError('Company name is required');
      return false;
    }
    if (!formData.name.trim()) {
      setError('Name is required');
      return false;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }
    if (!formData.password) {
      setError('Password is required');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    if (!formData.agreeToTerms) {
      setError('You must agree to the Terms & Conditions and Privacy Policy to create an account');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfoMessage('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const registrationData = {
        companyName: formData.companyName.trim(),
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password
      };

      const response = await api.post('/auth/register', registrationData);
      setVerificationState({
        email: registrationData.email,
        verificationEmailSent: Boolean(response.data?.verification_email_sent),
      });
      setInfoMessage(response.data?.message || 'Check your inbox to verify your email address before signing in.');
    } catch (error) {
      setError(error.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    const email = verificationState?.email || formData.email.trim().toLowerCase();
    if (!email) {
      setError('Enter an email address before requesting another verification email.');
      return;
    }

    setResendLoading(true);
    setError('');
    setInfoMessage('');

    try {
      const response = await api.authApi.resendVerification(email);
      setInfoMessage(response.data?.message || 'Verification email sent. Check your inbox.');
      setVerificationState((prev) => ({
        ...prev,
        email,
        verificationEmailSent: true,
      }));
    } catch (resendError) {
      setError(resendError.response?.data?.error || 'Could not resend the verification email. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const getPasswordStrength = () => {
    const password = formData.password;
    if (password.length === 0) return { score: 0, label: '', color: 'transparent' };
    if (password.length < 8) return { score: 25, label: 'Weak', color: theme.palette.error.main };
    if (password.length < 12) return { score: 50, label: 'Fair', color: theme.palette.warning.main };
    if (password.length < 16) return { score: 75, label: 'Good', color: theme.palette.info.main };
    return { score: 100, label: 'Strong', color: theme.palette.success.main };
  };

  const passwordsMatch = formData.password && formData.confirmPassword && formData.password === formData.confirmPassword;
  const passwordStrength = getPasswordStrength();
  const registrationPendingVerification = Boolean(verificationState?.email);

  return (
    <>
      <Helmet>
        <title>Start Free Trial — Struxor Construction Software</title>
        <meta name="description" content="Create your free Struxor account. Start using construction software for quotes, projects, timesheets, digital forms, and on-site collaboration in minutes." />
        <link rel="canonical" href="https://www.struxorapp.com/register" />
      </Helmet>
      <Box
      sx={{
        minHeight: '100vh',
        backgroundImage: 'url(/background_image.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: theme.palette.mode === 'light' 
            ? 'linear-gradient(180deg, rgba(17,24,39,0.45) 0%, rgba(17,24,39,0.6) 60%, rgba(17,24,39,0.75) 100%)'
            : 'linear-gradient(180deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.8) 60%, rgba(0,0,0,0.9) 100%)',
          zIndex: 1,
        },
      }}
    >
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 2 }}>
        {/* Dark Mode Toggle */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={darkMode}
                onChange={toggleDarkMode}
                icon={<LightModeIcon sx={{ color: 'rgba(255,255,255,0.8)' }} />}
                checkedIcon={<DarkModeIcon sx={{ color: 'rgba(255,255,255,0.8)' }} />}
              />
            }
            label=""
          />
        </Box>
        
        <Paper
          elevation={24}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            bgcolor: 'background.paper',
          }}
        >
          {/* Header Section */}
          <Box
            sx={{
              background: theme.palette.mode === 'light' 
                ? 'linear-gradient(45deg, #f8f9fa, #e9ecef)' 
                : `linear-gradient(45deg, ${alpha(theme.palette.background.paper, 0.8)}, ${alpha(theme.palette.background.default, 0.8)})`,
              color: 'text.primary',
              textAlign: 'center',
              py: 4,
              px: 3,
              position: 'relative',
            }}
          >
            <IconButton
              onClick={() => navigate('/login')}
              sx={{
                position: 'absolute',
                left: 16,
                top: 16,
                color: 'text.primary',
                '&:hover': {
                  background: alpha(theme.palette.text.primary, 0.1),
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            
            <Box
              component="img"
              src="/struxor.png"
              alt="Struxor logo"
              sx={{ 
                height: 56, 
                mb: 1.5, 
                filter: theme.palette.mode === 'dark' 
                  ? 'brightness(0) invert(1) drop-shadow(0 2px 2px rgba(0,0,0,0.3))' 
                  : 'drop-shadow(0 2px 2px rgba(0,0,0,0.1))' 
              }}
            />
            <Typography variant="h4" component="h1" fontWeight="600" gutterBottom>
              {registrationPendingVerification ? 'Verify Your Email' : 'Create Account'}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              {registrationPendingVerification
                ? 'Activate your account to finish sign-in setup'
                : 'Start managing your construction projects today'}
            </Typography>
          </Box>

          <CardContent sx={{ p: 4 }}>
            {registrationPendingVerification ? (
              <Box>
                {infoMessage && (
                  <Alert severity={verificationState.verificationEmailSent ? 'success' : 'warning'} sx={{ mb: 3, borderRadius: 2 }}>
                    {infoMessage}
                  </Alert>
                )}

                {error && (
                  <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                    {error}
                  </Alert>
                )}

                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <CheckCircle color="success" sx={{ fontSize: 56, mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Check {verificationState.email}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    We have created your account, but you need to verify your email address before you can sign in.
                  </Typography>
                </Box>

                <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                  <Typography variant="body2">
                    The verification link will take you back to Struxor and then you can sign in normally.
                  </Typography>
                </Alert>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleResendVerification}
                    disabled={resendLoading}
                    sx={{ py: 1.5, borderRadius: 2, fontWeight: 600 }}
                  >
                    {resendLoading ? 'Sending verification email...' : 'Resend Verification Email'}
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => navigate('/login')}
                    sx={{ py: 1.5, borderRadius: 2, fontWeight: 600 }}
                  >
                    Go to Sign In
                  </Button>
                  <Button
                    fullWidth
                    variant="text"
                    onClick={() => {
                      setVerificationState(null);
                      setInfoMessage('');
                      setError('');
                    }}
                    sx={{ borderRadius: 2, fontWeight: 500 }}
                  >
                    Use a Different Email
                  </Button>
                </Box>
              </Box>
            ) : (
            <form onSubmit={handleSubmit}>
              {error && (
                <Alert 
                  severity="error" 
                  sx={{ 
                    mb: 3,
                    borderRadius: 2,
                    '& .MuiAlert-icon': {
                      fontSize: '1.2rem'
                    }
                  }}
                >
                  {error}
                </Alert>
              )}

              {/* Company Information Section */}
              <Typography variant="h6" gutterBottom sx={{ color: theme.palette.primary.main, mb: 2 }}>
                Company Information
              </Typography>

              <Box sx={{ mb: 3, position: 'relative' }}>
                <TextField
                  fullWidth
                  label="Company Name"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                />
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Personal Information Section */}
              <Typography variant="h6" gutterBottom sx={{ color: theme.palette.primary.main, mb: 2 }}>
                Administrator Account
              </Typography>

              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  label="Full Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                />
                {formData.password && (
                  <Box sx={{ mt: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="caption" color="textSecondary">
                        Password strength: {passwordStrength.label}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={passwordStrength.score}
                      sx={{
                        height: 4,
                        borderRadius: 2,
                        backgroundColor: alpha(passwordStrength.color, 0.2),
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: passwordStrength.color,
                          borderRadius: 2,
                        },
                      }}
                    />
                  </Box>
                )}
              </Box>

              <Box sx={{ mb: 4 }}>
                <TextField
                  fullWidth
                  label="Confirm Password"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        {formData.confirmPassword && (
                          <Box sx={{ mr: 1 }}>
                            {passwordsMatch ? (
                              <CheckCircle color="success" fontSize="small" />
                            ) : (
                              <Cancel color="error" fontSize="small" />
                            )}
                          </Box>
                        )}
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          size="small"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                />
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Company Settings Info */}
              <Box sx={{ mb: 3 }}>
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  <Typography variant="body2" fontWeight={500} gutterBottom>
                    Company Branding & Details
                  </Typography>
                  <Typography variant="body2">
                    Once your account has been created, you can set up company branding, contact details, and other settings in the Company Settings section of Struxor.
                  </Typography>
                </Alert>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Terms and Conditions Agreement */}
              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="agreeToTerms"
                      checked={formData.agreeToTerms}
                      onChange={handleChange}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" color="text.secondary">
                      I agree to the Luke Rutley Ltd{' '}
                      <Button
                        component="a"
                        href="/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          p: 0,
                          minWidth: 'auto',
                          textTransform: 'none',
                          textDecoration: 'underline',
                          fontSize: 'inherit',
                          fontWeight: 'inherit',
                          color: theme.palette.primary.main,
                          '&:hover': {
                            textDecoration: 'underline',
                            background: 'transparent',
                          },
                        }}
                      >
                        Terms & Conditions
                      </Button>
                      {' '}and{' '}
                      <Button
                        component="a"
                        href="/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          p: 0,
                          minWidth: 'auto',
                          textTransform: 'none',
                          textDecoration: 'underline',
                          fontSize: 'inherit',
                          fontWeight: 'inherit',
                          color: theme.palette.primary.main,
                          '&:hover': {
                            textDecoration: 'underline',
                            background: 'transparent',
                          },
                        }}
                      >
                        Privacy Policy
                      </Button>
                    </Typography>
                  }
                  sx={{ alignItems: 'flex-start', mt: 1 }}
                />
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading || !formData.agreeToTerms}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: `0 4px 15px ${alpha(theme.palette.primary.main, 0.4)}`,
                  '&:hover': {
                    boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.6)}`,
                    transform: 'translateY(-1px)',
                  },
                  '&:disabled': {
                    background: theme.palette.grey[300],
                    boxShadow: 'none',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                {loading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} color="inherit" />
                    Creating Account...
                  </Box>
                ) : (
                  'Create Account'
                )}
              </Button>

              <Divider sx={{ my: 3 }}>
                <Typography variant="body2" color="textSecondary">
                  or
                </Typography>
              </Divider>

              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Already have an account?
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/login')}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 3,
                    mr: 1,
                    '&:hover': {
                      background: alpha(theme.palette.primary.main, 0.1),
                    },
                  }}
                >
                  Sign In
                </Button>
                <Button
                  variant="text"
                  onClick={() => navigate('/')}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 3,
                    '&:hover': {
                      background: alpha(theme.palette.primary.main, 0.1),
                    },
                  }}
                >
                  Home
                </Button>
              </Box>
            </form>
            )}
          </CardContent>
        </Paper>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <LegalBusinessDetails
            compact
            textAlign="center"
            textColor="rgba(255, 255, 255, 0.82)"
            linkColor="rgba(255, 255, 255, 0.95)"
            showLegalLinks
          />
        </Box>
      </Container>
    </Box>
    </>
  );
};

export default Register;